import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { fmtDateTime } from "../../lib/tz";
import { PageHeader, PillBadge, ActionButton, GhostButton } from "../../components/Primitives";
import { Clock, ShieldCheck, MapPin, ArrowRight, CalendarCheck, AlertTriangle } from "lucide-react";

const MS_HOUR = 60 * 60 * 1000;
const MS_DAY = 24 * MS_HOUR;

function startOfWeekUtc(now) {
  const d = new Date(now);
  d.setUTCHours(0, 0, 0, 0);
  const dow = d.getUTCDay(); // 0 = Sun
  const diff = (dow === 0 ? 6 : dow - 1); // Monday-start
  d.setUTCDate(d.getUTCDate() - diff);
  return d;
}

function parseIso(s) {
  try { return new Date((s || "").replace("Z", "+00:00")); } catch { return null; }
}

/**
 * DashboardGuard — slim self-service overview for guards.
 * Shows: My Next Shift · Hours This Week · Certs Expiring — plus a big CTA to
 * the Officer Portal where clock-in/out lives.
 */
export default function DashboardGuard() {
  const nav = useNavigate();
  const [guard, setGuard] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [me, s] = await Promise.all([
          api.get("/portal/me"),
          api.get("/portal/shifts"),
        ]);
        setGuard(me.data);
        setShifts(s.data || []);
      } catch (e) {
        setErr(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const now = useMemo(() => new Date(), []);
  const weekStart = useMemo(() => startOfWeekUtc(now), [now]);

  const nextShift = useMemo(() => {
    const nowIso = now.toISOString();
    const future = (shifts || [])
      .filter((s) => !s.checked_out_at && (s.start_time || "") > nowIso)
      .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
    return future[0] || null;
  }, [shifts, now]);

  const activeShift = useMemo(() => (shifts || []).find((s) => s.checked_in_at && !s.checked_out_at) || null, [shifts]);

  const hoursThisWeek = useMemo(() => {
    let ms = 0;
    (shifts || []).forEach((s) => {
      const inT = parseIso(s.checked_in_at);
      if (!inT || inT < weekStart) return;
      const outT = parseIso(s.checked_out_at) || now;
      if (outT <= inT) return;
      ms += outT - inT;
    });
    return ms / MS_HOUR;
  }, [shifts, weekStart, now]);

  const certExpiring = useMemo(() => {
    const list = Array.isArray(guard?.certifications) ? guard.certifications : [];
    const soon = now.getTime() + 30 * MS_DAY;
    const rows = [];
    list.forEach((c) => {
      if (!c || typeof c !== "object") return;
      if (c.no_expiry) return;
      const exp = parseIso(c.expires_at);
      if (!exp) return;
      const daysLeft = Math.round((exp.getTime() - now.getTime()) / MS_DAY);
      if (daysLeft <= 30) {
        rows.push({ name: c.name || "Certification", daysLeft, expired: daysLeft < 0 });
      }
    });
    return rows.sort((a, b) => a.daysLeft - b.daysLeft);
  }, [guard, now]);

  return (
    <div>
      <PageHeader
        title={guard?.name ? `Hi, ${guard.name.split(" ")[0]}` : "Overview"}
        subtitle="Your day-at-a-glance. Head to the Officer Portal when you&apos;re ready to clock in or file reports."
      />

      <div className="p-8 space-y-8" data-testid="dashboard-guard-root">
        {err && (
          <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/5 px-4 py-3 text-[#FF3B30] text-sm">
            Couldn&apos;t load your shifts. Try refreshing.
          </div>
        )}

        {/* Slim single-card layout */}
        <section className="border border-[#27272A] bg-[#121212]">
          <div className="px-6 py-5 border-b border-[#27272A] flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="label-mute">On Shift</div>
              <div className="font-display uppercase font-bold text-2xl mt-1">
                {activeShift ? "Clocked In Right Now" : nextShift ? "Next Shift Up" : "No Shift Scheduled"}
              </div>
            </div>
            <ActionButton
              onClick={() => nav("/guard-portal")}
              data-testid="dashboard-guard-portal-cta"
            >
              Open Officer Portal
              <ArrowRight className="size-4" />
            </ActionButton>
          </div>

          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Next Shift */}
            <div data-testid="dashboard-guard-next-shift" className="space-y-2">
              <div className="flex items-center gap-2 label-mute">
                <CalendarCheck className="size-3.5" />
                {activeShift ? "Current shift" : "My next shift"}
              </div>
              {loading ? (
                <div className="text-zinc-500 text-sm">Loading…</div>
              ) : (activeShift || nextShift) ? (
                (() => {
                  const s = activeShift || nextShift;
                  const startTxt = fmtDateTime(s.start_time, s.site_timezone);
                  return (
                    <div>
                      <div className="text-lg font-bold text-zinc-100 truncate">
                        {s.site_name || "Site TBD"}
                      </div>
                      <div className="text-sm text-zinc-400 mt-1 flex items-center gap-1.5">
                        <MapPin className="size-3.5 text-zinc-600" />
                        {s.site_address || "—"}
                      </div>
                      <div className="text-xs font-mono text-zinc-500 mt-2">
                        {activeShift ? "Started" : "Starts"} · {startTxt}
                      </div>
                      {activeShift && (
                        <PillBadge tone="green" className="mt-3">On Duty</PillBadge>
                      )}
                    </div>
                  );
                })()
              ) : (
                <div className="text-zinc-500 text-sm">
                  Nothing on the schedule. Enjoy the break.
                </div>
              )}
            </div>

            {/* Hours This Week */}
            <div data-testid="dashboard-guard-hours-week" className="space-y-2">
              <div className="flex items-center gap-2 label-mute">
                <Clock className="size-3.5" />
                Hours this week
              </div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="font-mono font-black text-4xl tracking-tighter text-[#34C759]">
                  {hoursThisWeek.toFixed(1)}
                </span>
                <span className="text-zinc-500 text-xs uppercase tracking-widest">hrs logged</span>
              </div>
              <div className="text-xs text-zinc-500">
                Since Monday · {weekStart.toISOString().slice(0, 10)}
              </div>
            </div>

            {/* Certs Expiring */}
            <div data-testid="dashboard-guard-certs" className="space-y-2">
              <div className="flex items-center gap-2 label-mute">
                <ShieldCheck className="size-3.5" />
                Certifications
              </div>
              {loading ? (
                <div className="text-zinc-500 text-sm">Loading…</div>
              ) : certExpiring.length === 0 ? (
                <div className="text-sm text-zinc-300">
                  <PillBadge tone="green" className="mr-2">Good</PillBadge>
                  All current for the next 30 days.
                </div>
              ) : (
                <ul className="space-y-1.5">
                  {certExpiring.slice(0, 3).map((c) => (
                    <li key={c.name} className="flex items-start gap-2 text-sm">
                      <AlertTriangle className={`size-4 mt-0.5 shrink-0 ${c.expired ? "text-[#FF3B30]" : "text-[#FF9F0A]"}`} />
                      <div>
                        <div className={`${c.expired ? "text-[#FF3B30]" : "text-zinc-100"}`}>
                          {c.name}
                        </div>
                        <div className="text-xs text-zinc-500">
                          {c.expired
                            ? `Expired ${-c.daysLeft}d ago`
                            : `Expires in ${c.daysLeft}d`}
                        </div>
                      </div>
                    </li>
                  ))}
                  {certExpiring.length > 3 && (
                    <li className="text-xs text-zinc-500 pl-6">
                      + {certExpiring.length - 3} more
                    </li>
                  )}
                </ul>
              )}
            </div>
          </div>

          <div className="px-6 py-4 border-t border-[#27272A] flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest text-zinc-500">
              Need something else?
            </div>
            <div className="flex items-center gap-2">
              <GhostButton onClick={() => nav("/availability")} data-testid="dashboard-guard-availability">
                My Availability
              </GhostButton>
              <GhostButton onClick={() => nav("/dar")} data-testid="dashboard-guard-dar">
                File a DAR
              </GhostButton>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
